import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {toCssSize} from '../../lib/utils';
import {FieldErrorMessage} from '../../field-error-message';

type SelectFieldShellProps = {
  containerRef?: React.Ref<HTMLDivElement>;
  className?: string;
  width?: number | string;
  disabled?: boolean;
  loading?: boolean;
  topLabel?: string;
  triggerId: string;
  errorId: string;
  errorMessage?: string;
  hideErrorMessage?: boolean;
  optional?: boolean | string;
  helperText?: string;
  name?: string;
  hiddenValue?: string;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onContentClick?: React.MouseEventHandler<HTMLDivElement>;
  children: React.ReactNode;
};

const FOCUSABLE_TRIGGER_SELECTOR =
  'input:not([type="hidden"]):not([disabled]):not([readonly]), button:not([disabled])';

function SelectFieldShell({
  containerRef,
  className,
  width,
  disabled,
  loading,
  topLabel,
  triggerId,
  errorId,
  errorMessage,
  hideErrorMessage,
  optional,
  helperText,
  name,
  hiddenValue,
  onBlur,
  onContentClick,
  children,
}: SelectFieldShellProps) {
  const {t} = useTranslation();
  const wrapperWidth = toCssSize(width);

  const handleContainerFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const focusable = event.currentTarget.querySelector<HTMLElement>(
      FOCUSABLE_TRIGGER_SELECTOR,
    );
    focusable?.focus();
  };

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      onBlur={onBlur}
      onFocus={handleContainerFocus}
      className={cn(
        'relative w-[var(--field-width,100%)] max-w-[var(--field-max-width,296px)] outline-none',
        disabled && !loading && 'cursor-not-allowed opacity-50',
        loading && 'cursor-progress opacity-50',
        className,
      )}
      style={wrapperWidth ? {width: wrapperWidth} : undefined}
    >
      {name && <input type="hidden" name={name} value={hiddenValue ?? ''} />}
      <div className="relative min-h-[var(--field-min-height,48px)] w-full">
        {topLabel && (
          <label
            htmlFor={triggerId}
            className="mb-2 block text-[length:var(--field-top-label-font-size)] font-[var(--field-top-label-font-weight)] text-[var(--chekin-color-brand-navy)]"
          >
            {topLabel}
          </label>
        )}
        <div className="relative w-full" onClick={onContentClick}>
          {children}
        </div>
        {!errorMessage && optional && (
          <span className="mt-[1px] block text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]">
            {typeof optional === 'string' ? optional : t('optional')}
          </span>
        )}
        {!errorMessage && helperText && (
          <span className="mt-[1px] block text-[12px] font-normal text-[var(--chekin-color-gray-1)]">
            {helperText}
          </span>
        )}
        {errorMessage && !hideErrorMessage && (
          <FieldErrorMessage
            id={errorId}
            message={errorMessage}
            className="mt-[1px] text-[14px]"
            data-testid={name ? `${name}-error` : undefined}
          />
        )}
      </div>
    </div>
  );
}

export {SelectFieldShell};
export type {SelectFieldShellProps};
